/**
 * Groups API - Per-viewer notification preferences (READ)
 *
 * `GET /v1/groups/:group_id/preferences` — the session viewer's OWN persisted
 * notification level for one group. The read counterpart of
 * `POST /v1/groups/:group_id/preferences`, which mobile already writes through.
 *
 * Why this contract exists: the mobile chat "Mute notifications" switch used to
 * seed itself from `schoolUser.groups[]` on the session payload, but
 * `GET /v1/school-users/current` never populates that array (its `includeGroups`
 * branch is an unimplemented stub). The switch therefore rendered OFF on every
 * mount regardless of what was stored, so a mute looked like it never took.
 * Sourcing the toggle from this endpoint reads the same store the write lands in.
 *
 * `data` is nullable: the server returns `null` when the viewer has neither
 * notification preferences nor a role/status row for the group — a real answer
 * ("nothing stored"), not an error.
 */

import { z } from 'zod'
import { NotificationLevelSchema } from './common'

// ============================================================================
// GET /v1/groups/:group_id/preferences
// ============================================================================

export const GroupUserPreferencesSchema = z.object({
  groupId: z.string(),
  userId: z.string(),
  notificationLevel: NotificationLevelSchema.nullable(),
  notifyPush: z.boolean().nullable(),
  notifyEmail: z.boolean().nullable(),
  notifySms: z.boolean().nullable(),
  role: z.string().nullable(),
  status: z.string().nullable()
})

export type GroupUserPreferences = z.infer<typeof GroupUserPreferencesSchema>

export const GetGroupUserPreferencesResponseSchema = z.object({
  data: GroupUserPreferencesSchema.nullable(),
  message: z.string()
})

export type GetGroupUserPreferencesResponse = z.infer<typeof GetGroupUserPreferencesResponseSchema>
